import { useEffect, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  notificationEventsApi,
  notificationCellsApi,
  notificationSettingsApi,
  notificationSettingsQueryKeys,
  notificationVariablesApi,
  type MessagingTemplate,
  type NotificationEventRow,
  type ZbsReadiness,
} from "../../shared/api/notificationSettings";
import { apiError } from "../../shared/api/client";
import { PageHeader } from "../../shared/ui/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScopeBar } from "./ScopeBar";
import { SendingChannels } from "./SendingChannels";
import { ChannelSwitches } from "./ChannelSwitches";
import { useMessagingChannels } from "./ChannelConnection";
import {
  EventTable,
  FILTER_PARAM,
  FilterChips,
  filterFromParam,
  matchesFilter,
  verdictsFor,
  type Filter,
} from "./EventTable";
import { failureLabel, totalsFor } from "./notificationHealth";
import { EventChannelTemplates, SWITCH, templateName } from "./EventChannelTemplates";
import { OtpLookupNotice, RESULTS_LOOKUP_KEY, useResultsLookup } from "./OtpLookupNotice";

const CHANNEL_LABEL: Record<string, string> = {
  ZBS: "Zalo ZBS",
  SMS: "SMS",
  EMAIL: "Email",
};

const AUDIENCE_LABEL: Record<string, string> = {
  PATIENT: "Bệnh nhân",
  GUARDIAN: "Người giám hộ",
  STAFF_ROLE: "Nhân viên",
};

/**
 * Templates that can actually be sent on one channel (#18933).
 *
 * <p>Both halves matter and they fail differently. The channel match is why a ZBS picker must never
 * list an SMS template: a template is a set of per-channel renditions at the provider, and binding
 * a code whose ZBS rendition does not exist produces a send with no template at all. The approval
 * match is why an unapproved one is hidden: the provider refuses it at send time, long after the
 * person choosing it has left the screen.
 */
export function approvedTemplatesForChannel(
  templates: MessagingTemplate[],
  channel: string,
): MessagingTemplate[] {
  return templates.filter((t) =>
    t.renditions.some(
      (r) => r.channel === channel && r.approvalStatus === "APPROVED",
    ),
  );
}

/** Approved ZBS renditions only — kept for the per-event picker, which is ZBS-only. */
export function approvedZbsTemplates(
  templates: MessagingTemplate[],
): MessagingTemplate[] {
  return approvedTemplatesForChannel(templates, "ZBS");
}

/**
 * Required parameters of one channel's rendition (#18933).
 *
 * <p>Distinct from {@link requiredParamsOf}, which unions every channel: the SMS rendition of a
 * template may require parameters the ZBS one does not, and checking the union would refuse a
 * perfectly valid ZBS binding because of a sibling rendition the send will never touch.
 */
export function requiredParamsForChannel(
  t: MessagingTemplate | undefined,
  channel: string,
): string[] {
  if (!t) return [];
  const keys = new Set<string>();
  for (const r of t.renditions) {
    if (r.channel !== channel) continue;
    for (const p of r.params) if (p.required) keys.add(p.key);
  }
  return [...keys];
}

/**
 * The fields a template would need that this event cannot fill — the same rule the server applies
 * when the clinic saves (`NotificationEventConfigController.missingParams`): every rendition's
 * canonical fields plus its required parameters, minus `event.fillableParams`.
 *
 * <p>Flattened across channels like the server, so the picker never offers what saving refuses.
 * Null when the API is too old to publish `fillableParams` — then nothing is pre-judged and the
 * save answers, as before.
 */
export function missingParamsForEvent(
  t: MessagingTemplate,
  event: NotificationEventRow,
): string[] | null {
  if (!event.fillableParams) return null;
  const need = new Set<string>();
  for (const r of t.renditions) {
    for (const c of r.canonicalParams ?? []) need.add(c);
    for (const p of r.params) if (p.required) need.add(p.canonicalKey ?? p.key);
  }
  const fillable = new Set(event.fillableParams);
  return [...need].filter((k) => !fillable.has(k));
}

/**
 * Every field a template's renditions on `channel` use, required or not — the same reading as the
 * server's `fieldsUsed` (#19564). `channel` null unions every rendition. Null when the template
 * declares nothing there, which means "cannot tell", never "carries nothing".
 */
export function fieldsUsedBy(
  t: MessagingTemplate,
  channel: string | null,
): Set<string> | null {
  let out: Set<string> | null = null;
  for (const r of t.renditions) {
    if (channel !== null && r.channel !== channel) continue;
    const found = [
      ...(r.canonicalParams ?? []),
      ...r.params.map((p) => p.canonicalKey ?? p.key),
    ];
    if (found.length === 0) continue;
    out = out ?? new Set<string>();
    for (const f of found) out.add(f);
  }
  return out;
}

/**
 * The event's essential fields the template does not carry (#19564) — empty when one group is
 * there in full, or when nothing can be judged. The server refuses exactly these on save.
 */
export function lackingEssential(
  t: MessagingTemplate,
  event: NotificationEventRow,
  channel: string | null,
): string[][] {
  const groups = event.essentialFields ?? [];
  const used = fieldsUsedBy(t, channel);
  if (!used || groups.length === 0) return [];
  return groups.some((g) => g.every((f) => used.has(f))) ? [] : groups;
}

export function requiredParamsOf(t: MessagingTemplate | undefined): string[] {
  if (!t) return [];
  const keys = new Set<string>();
  for (const r of t.renditions) {
    for (const p of r.params) if (p.required) keys.add(p.key);
  }
  return [...keys];
}

/**
 * What the server decided about sending this event on Zalo (#19209), next to the event — so an
 * event that is ticked but can never go out says so here instead of in a patient's silence.
 * Nothing is shown for a switched-off event: there is nothing to warn about.
 */
const ZBS_READINESS: Record<
  ZbsReadiness,
  { text: string; tone: "ok" | "warn" | "error" | "muted" } | null
> = {
  READY: { text: "● Zalo sẵn sàng", tone: "ok" },
  DISABLED: null,
  CHANNEL_OFF: { text: "Zalo đang tắt", tone: "muted" },
  NO_TEMPLATE: {
    text: "⚠️ Chưa chọn mẫu Zalo chung — sẽ không gửi",
    tone: "warn",
  },
  TEMPLATE_NOT_FOUND: { text: "⚠️ Mẫu Zalo không còn tồn tại", tone: "warn" },
  TEMPLATE_ARCHIVED: { text: "⚠️ Mẫu Zalo đã lưu trữ", tone: "warn" },
  TEMPLATE_NOT_APPROVED: { text: "⚠️ Mẫu Zalo chưa được duyệt", tone: "warn" },
  TEMPLATE_UNSUITABLE: {
    text: "⛔ Mẫu Zalo không đúng mục đích sự kiện",
    tone: "error",
  },
  PARAMS_MISSING: { text: "⚠️ Mẫu Zalo thiếu tham số", tone: "warn" },
  UNKNOWN: { text: "Chưa kiểm tra được mẫu Zalo", tone: "muted" },
};

/** Why a non-Zalo channel will not send (#19566) — only the states worth a word on screen. */
const CHANNEL_PROBLEM: Partial<Record<ZbsReadiness, string>> = {
  NO_TEMPLATE: "chưa chọn mẫu",
  TEMPLATE_NOT_FOUND: "mẫu không còn tồn tại",
  TEMPLATE_ARCHIVED: "mẫu đã lưu trữ",
  TEMPLATE_NOT_APPROVED: "mẫu chưa được duyệt",
  TEMPLATE_UNSUITABLE: "mẫu không đúng mục đích",
  PARAMS_MISSING: "mẫu thiếu tham số",
};

/** Why a cell will not send, in a few words; null when it can (or is simply off). */
export function readinessProblem(
  r: ZbsReadiness | null | undefined,
): string | null {
  return (r && CHANNEL_PROBLEM[r]) ?? null;
}

/** "SMS: chưa chọn mẫu" for each channel other than Zalo that is on and cannot send. */
export function ChannelProblems({
  readiness,
}: {
  readiness?: Record<string, ZbsReadiness> | null;
}) {
  const items = Object.entries(readiness ?? {}).filter(
    ([ch, r]) => ch !== "ZBS" && CHANNEL_PROBLEM[r],
  );
  if (items.length === 0) return null;
  return (
    <>
      {items.map(([ch, r]) => (
        <span
          key={ch}
          className={
            "rounded px-1.5 py-0.5 text-xs font-normal " +
            (r === "TEMPLATE_UNSUITABLE"
              ? "bg-red-50 text-red-700"
              : "bg-amber-50 text-amber-800")
          }
          data-testid={`channel-problem-${ch}`}
        >
          ⚠️ {CHANNEL_LABEL[ch] ?? ch}: {CHANNEL_PROBLEM[r]}
        </span>
      ))}
    </>
  );
}

export function ZbsReadinessBadge({
  readiness,
}: {
  readiness?: ZbsReadiness | null;
}) {
  const r = readiness ? ZBS_READINESS[readiness] : null;
  if (!r) return null;
  const tone =
    r.tone === "ok"
      ? "bg-emerald-50 text-emerald-700"
      : r.tone === "warn"
        ? "bg-amber-50 text-amber-800"
        : r.tone === "error"
          ? "bg-red-50 text-red-700"
          : "bg-muted text-muted-foreground";
  return (
    <span
      className={`rounded px-1.5 py-0.5 text-xs font-normal ${tone}`}
      data-testid="zbs-readiness"
    >
      {r.text}
    </span>
  );
}

/** T85 — Cài đặt thông báo. Real /api/v1/clinic/settings/notifications. */
export function NotificationsSettingsPage() {
  const qc = useQueryClient();
  // #19667 — the same shared query the channel cards read, so a cell and its card never disagree.
  const messagingChannels = useMessagingChannels();
  const q = useQuery({
    queryKey: notificationSettingsQueryKeys.all(),
    queryFn: () => notificationSettingsApi.get(),
  });

  // Two audiences, two tabs (#18933 §5). Split rather than filtered in place: a patient template
  // must never be offered for a staff event, and separating them on screen is what makes that
  // obvious to the person configuring rather than only true in the API.
  const [tab, setTab] = useState<"PATIENT" | "STAFF">("PATIENT");
  // #19578 — which row of the overview is open; one at a time.
  const [openRow, setOpenRow] = useState<string | null>(null);
  // #19585 — which scope the open row edits: the clinic default (null) or one service. Replaces
  // the per-service matrix (#18933), whose first row was a second editor for the default.
  const [scope, setScope] = useState<string | null>(null);
  // #19579 — an open row holding an unsaved fixed-value draft asks before it closes.
  const [dirtyPickers, setDirtyPickers] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState<{ run: () => void } | null>(
    null,
  );
  /**
   * Run an action that would throw away an unsaved fixed-value draft — ask first (review of
   * #19579). `drops` says whether this action actually takes the draft off the screen: a filter
   * that keeps the open row visible does not, and must not ask about a draft it leaves alone.
   */
  const guard = (drops: boolean, run: () => void) => {
    if (drops && dirtyPickers) setConfirmLeave({ run });
    else run();
  };
  const goToRow = (to: string | null) => {
    setOpenRow(to);
    setScope(null);
    setDirtyPickers(false);
  };
  // Changing scope unmounts the pickers holding the draft, so it is guarded like closing the row.
  const requestScope = (next: string | null) => {
    if (next === scope) return;
    guard(true, () => {
      setScope(next);
      setDirtyPickers(false);
    });
  };
  const requestRow = (to: string | null) => {
    if (to === openRow) return;
    guard(true, () => goToRow(to));
  };
  // #19578 — the verdict filter lives in the URL (?loc=loi) so a filtered view can be shared.
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = filterFromParam(searchParams.get("loc"));
  const setFilter = (f: Filter) => {
    const next = new URLSearchParams(searchParams);
    if (FILTER_PARAM[f]) next.set("loc", FILTER_PARAM[f]!);
    else next.delete("loc");
    setSearchParams(next, { replace: true });
  };
  const [msg, setMsg] = useState<string | null>(null);
  // #19613 — the website lookup depends on the OTP: switching the OTP off while the lookup is on
  // hides the website form, so the clinic is asked first. An in-page dialog, not window.confirm.
  const resultsLookup = useResultsLookup();
  const [confirmOtpOff, setConfirmOtpOff] = useState<NotificationEventRow | null>(null);
  const [error, setError] = useState<string | null>(null);
  const location = useLocation();

  // #19577 — the channel switches save on click (ChannelSwitches); there is no form to hold.
  const form = q.data ?? null;

  // Per-event configuration (#18753). Kept as its own query so a messaging-platform outage
  // degrades this section only — the channel switches above stay usable.
  const eventsQ = useQuery({
    queryKey: notificationSettingsQueryKeys.events(),
    queryFn: () => notificationEventsApi.list(),
  });
  const templatesQ = useQuery({
    queryKey: notificationSettingsQueryKeys.templates(),
    queryFn: () => notificationEventsApi.templates(),
  });
  // #19566 — what actually happened, next to what the configuration predicts. A failure here hides
  // the counts and nothing else.
  const STATS_DAYS = 7;
  const statsQ = useQuery({
    queryKey: notificationSettingsQueryKeys.deliveryStats(STATS_DAYS),
    queryFn: () => notificationEventsApi.deliveryStats(STATS_DAYS),
  });
  const stats = statsQ.data ?? [];

  // #19565 — Vietnamese labels for the fields ("Tên khách hàng", not `patient.name`). Decoration:
  // without the catalogue the identifiers still show, so a failure here degrades nothing else.
  const variablesQ = useQuery({
    queryKey: notificationSettingsQueryKeys.variables(),
    queryFn: () => notificationVariablesApi.list(),
  });
  const labels: Record<string, string> = Object.fromEntries(
    (variablesQ.data ?? []).filter((v) => v.label).map((v) => [v.key, v.label]),
  );
  const examples: Record<string, string> = Object.fromEntries(
    (variablesQ.data ?? [])
      .filter((v) => v.example)
      .map((v) => [v.key, v.example]),
  );

  const templates = approvedZbsTemplates(templatesQ.data ?? []);

  // #19578 — the overview shows each event's clinic-wide template per channel. Same query keys as
  // the pickers in the open row, so opening a row costs no request.
  const patientEvents = (eventsQ.data ?? []).filter(
    (e) => e.audience !== "STAFF_ROLE",
  );
  // #19667 D8 — one request for the whole table. Its rows seed each event's own cache entry (the
  // key the pickers and every save already use), so nothing downstream changes; if it fails, each
  // event falls back to asking for itself.
  const allCellsQ = useQuery({
    queryKey: notificationSettingsQueryKeys.allCells(),
    queryFn: notificationCellsApi.listAll,
    retry: false,
  });
  const cellQueries = useQueries({
    queries: patientEvents.map((e) => ({
      queryKey: notificationSettingsQueryKeys.cells(e.eventKey),
      queryFn: () => notificationCellsApi.list(e.eventKey),
      enabled: !allCellsQ.isPending,
      initialData: allCellsQ.data?.[e.eventKey],
      initialDataUpdatedAt: allCellsQ.dataUpdatedAt,
      staleTime: 30_000,
    })),
  });
  // undefined = still loading, null = could not load — neither may read as "no template".
  const cellsFor = (eventKey: string) => {
    const q =
      cellQueries[patientEvents.findIndex((e) => e.eventKey === eventKey)];
    return q?.isError ? null : q?.data;
  };
  const verdicts = verdictsFor(patientEvents, statsQ.isSuccess ? stats : null);

  // Deep link from the Website screen's "Chọn mẫu OTP" (#19180): /settings/notifications#PATIENT_OTP
  // must land on the patient tab AND on the OTP row, or the clinic is left hunting for it.
  // Once per anchor, and only after the template catalogue has settled: the pickers above the
  // target grow the page while they load, and scrolling earlier lands short. Without the ref,
  // every save (which refetches the events) would yank the page back to the anchor.
  const anchor = location.hash.replace(/^#/, "");
  const scrolledFor = useRef<string | null>(null);
  const settled = eventsQ.isSuccess && !!form && !templatesQ.isLoading;
  useEffect(() => {
    if (!anchor || !settled || scrolledFor.current === anchor) return;
    const target = eventsQ.data?.find((e) => e.eventKey === anchor);
    if (!target) return;
    scrolledFor.current = anchor;
    setTab(target.audience === "STAFF_ROLE" ? "STAFF" : "PATIENT");
    // #19579 — and opens it: the link exists to bring the clinic to that event's settings. A
    // filter that would hide it is cleared, or the link would land on a list without it.
    if (target.audience !== "STAFF_ROLE") {
      if (!matchesFilter(filter, verdicts.get(anchor) ?? "ok"))
        setFilter("all");
      setOpenRow(anchor);
    }
    // Not cleared on re-run: the ref already says "done", so a cleared timer would mean no scroll
    // at all. After unmount it finds no element and does nothing.
    window.setTimeout(
      () =>
        document
          .getElementById(`event-${anchor}`)
          ?.scrollIntoView({ block: "start" }),
      300,
    );
    // Once per anchor (scrolledFor): the filter and verdicts are read at that moment, not
    // tracked — re-running on every filter change would yank the page back to the anchor.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchor, settled, eventsQ.data]);

  const saveEvent = useMutation({
    mutationFn: (v: {
      row: NotificationEventRow;
      templateCode: string | null;
      enabled: boolean;
    }) =>
      notificationEventsApi.update(v.row.eventKey, {
        enabled: v.enabled,
        channelOrder: v.row.channelOrder,
        templateCode: v.templateCode,
        // Snapshot the template's required params at binding time, so a template edited at Zalo
        // later can be spotted instead of quietly sending blank fields.
        boundParams: requiredParamsOf(
          templates.find((t) => t.code === v.templateCode),
        ),
      }),
    onSuccess: (_d, v) => {
      qc.invalidateQueries({
        queryKey: notificationSettingsQueryKeys.events(),
      });
      if (v.row.eventKey === "PATIENT_OTP") {
        qc.invalidateQueries({ queryKey: RESULTS_LOOKUP_KEY });
      }
      setMsg("Đã lưu cài đặt sự kiện.");
      setError(null);
    },
    onError: (e: unknown) => {
      setError(apiError(e).detail);
      setMsg(null);
    },
  });

  /**
   * What an open row shows (#19578): everything the per-event block used to show, unchanged —
   * the event's facts, a template picker per channel, the week's outcome and the per-service
   * matrix. Only where it sits has moved; #19579 rearranges it.
   */
  const renderDetail = (ev: NotificationEventRow) => (
    <div
      className="grid gap-4"
      data-testid="event-detail"
    >
      {ev.eventKey === "PATIENT_OTP" && <OtpLookupNotice enabled={ev.enabled} />}
      {/* #19585 — the scope comes first: everything below is about it. The OTP has none. */}
      {!ev.systemMessage && (
        <div className="border-b pb-3">
          <ScopeBar
            event={ev}
            templates={templatesQ.data ?? []}
            scope={scope}
            channels={["ZBS", "SMS", "EMAIL"].filter((c) => form && form[SWITCH[c]])}
            onScope={requestScope}
            onError={showError}
          />
        </div>
      )}
      <div className="grid min-w-0 content-start gap-2">
        <p className="text-xs text-muted-foreground">
          Gửi tới: {AUDIENCE_LABEL[ev.audience] ?? ev.audience}
          {" · "}
          {ev.systemMessage ? (
            "Gửi Zalo trước, không tới thì gửi SMS."
          ) : (
            <SendingChannels event={ev} settings={form} serviceId={scope} />
          )}
        </p>
        {/* Each channel's picker carries its own reason when its template is wrong — said once,
            there, rather than again in a column beside it (#19579). */}
        <EventChannelTemplates
          // A new scope is a new set of drafts: remounting drops the previous one's, which the
          // guard has already asked about.
          key={scope ?? "__default__"}
          serviceId={ev.systemMessage ? null : scope}
          event={ev}
          templates={templatesQ.data ?? []}
          templatesReady={templatesQ.isSuccess}
          labels={labels}
          examples={examples}
          settings={form}
          onDirtyChange={setDirtyPickers}
          onSaved={(m) => {
            setMsg(m);
            setError(null);
          }}
          onError={showError}
        />
      </div>
      {/* #19587 — the week's outcome is the footer: each channel card now holds both the
          configuration and what the patient receives, so there is no side column left. */}
      {statsQ.isSuccess && (
        <div className="border-t pt-2">
          <DeliveryLine
            totals={totalsFor(ev.eventKey, stats)}
            days={STATS_DAYS}
          />
        </div>
      )}
    </div>
  );

  // An error replaces the last success line rather than sitting next to it (#19211): "Đã lưu…"
  // beside "chưa dùng được" left the clinic unsure which of the two had happened.
  const showError = (m: string | null) => {
    setError(m);
    if (m) setMsg(null);
  };

  return (
    <div className="font-sans">
      <PageHeader
        title="Cài đặt thông báo"
        description="Chọn kênh gửi và loại thông báo cho phòng khám."
      />

      {q.isLoading && (
        <p className="text-sm text-muted-foreground">Đang tải…</p>
      )}
      {q.isError && (
        <p className="text-sm text-destructive">Không tải được cài đặt.</p>
      )}

      {form && (
        // #19577 — max-w-6xl: the page is a table now, and at 672px half of a 1920px screen sat
        // empty while the content was crushed into two narrow columns.
        <div className="grid max-w-6xl gap-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Kênh gửi</CardTitle>
              <p className="text-xs text-muted-foreground">
                Bật / tắt được lưu ngay.
              </p>
            </CardHeader>
            <CardContent className="pt-0">
              <ChannelSwitches settings={form} events={eventsQ.data ?? []} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                Loại thông báo &amp; mẫu tin
              </CardTitle>
              <p className="text-xs text-muted-foreground">
                Chọn sự kiện muốn gửi, rồi chọn mẫu tin cho <b>từng kênh</b>{" "}
                đang bật ở trên. Mỗi ô chỉ liệt kê mẫu đã được duyệt cho đúng
                kênh đó, xếp theo mức phù hợp: mẫu phải nói đúng việc của sự
                kiện (vd. nhắc lịch phải có ngày giờ khám) và chỉ dùng thông tin
                hệ thống điền được — mẫu không phù hợp sẽ không chọn được.
              </p>
            </CardHeader>
            <CardContent className="grid gap-3 pt-0">
              {eventsQ.isLoading && (
                <p className="text-sm text-muted-foreground">
                  Đang tải danh sách sự kiện…
                </p>
              )}
              {eventsQ.isError && (
                <p className="text-sm text-destructive">
                  Không tải được danh sách sự kiện.
                </p>
              )}
              {templatesQ.isSuccess && templates.length === 0 && (
                <p className="rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
                  Chưa lấy được mẫu tin nào từ Addy Message. Phòng khám vẫn
                  bật/tắt được sự kiện, nhưng mỗi kênh cần được kết nối (Zalo OA,
                  nhà cung cấp SMS, máy chủ Email) và có mẫu được duyệt trước khi
                  gửi được tin.
                </p>
              )}

              <div className="flex gap-1 border-b">
                {(
                  [
                    ["PATIENT", "Khách hàng / Bệnh nhân"],
                    ["STAFF", "Nhân viên"],
                  ] as const
                ).map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() =>
                      key !== tab && guard(true, () => setTab(key))
                    }
                    className={
                      "-mb-px border-b-2 px-3 py-2 text-sm " +
                      (tab === key
                        ? "border-primary font-medium text-foreground"
                        : "border-transparent text-muted-foreground")
                    }
                  >
                    {label}
                  </button>
                ))}
              </div>

              {tab === "PATIENT" && eventsQ.data && (
                <>
                  <FilterChips
                    verdicts={[...verdicts.values()]}
                    value={filter}
                    onChange={(f) =>
                      guard(
                        !!openRow &&
                          !matchesFilter(f, verdicts.get(openRow) ?? "ok"),
                        () => setFilter(f),
                      )
                    }
                  />
                  <EventTable
                    connection={messagingChannels.data?.channels}
                    events={patientEvents.filter((ev) =>
                      matchesFilter(filter, verdicts.get(ev.eventKey) ?? "ok"),
                    )}
                    cellsFor={cellsFor}
                    stats={statsQ.isSuccess ? stats : null}
                    expanded={openRow}
                    onToggleExpand={(key) =>
                      requestRow(openRow === key ? null : key)
                    }
                    savingEnabled={saveEvent.isPending}
                    onToggleEnabled={(ev, enabled) => {
                      setMsg(null);
                      setError(null);
                      if (
                        ev.eventKey === "PATIENT_OTP" &&
                        !enabled &&
                        resultsLookup.data?.enabled
                      ) {
                        setConfirmOtpOff(ev);
                        return;
                      }
                      saveEvent.mutate({
                        row: ev,
                        templateCode: ev.templateCode,
                        enabled,
                      });
                    }}
                    renderDetail={(ev) => renderDetail(ev)}
                    nameOf={(code, channel) =>
                      templateName(
                        (templatesQ.data ?? []).find((t) => t.code === code),
                        channel,
                      )
                    }
                  />
                </>
              )}

              {tab === "STAFF" && (
                <StaffEvents
                  events={(eventsQ.data ?? []).filter(
                    (ev) => ev.audience === "STAFF_ROLE",
                  )}
                  saving={saveEvent.isPending}
                  onToggle={(ev, enabled) => {
                    setMsg(null);
                    setError(null);
                    saveEvent.mutate({
                      row: ev,
                      templateCode: ev.templateCode,
                      enabled,
                    });
                  }}
                />
              )}
            </CardContent>
          </Card>

          {error && <p className="text-sm text-destructive">{error}</p>}
          {msg && <p className="text-sm text-emerald-600">{msg}</p>}

          <Dialog
            open={!!confirmLeave}
            onOpenChange={(o) => !o && setConfirmLeave(null)}
          >
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Bỏ giá trị cố định chưa lưu?</DialogTitle>
                <DialogDescription>
                  Bạn đang nhập giá trị cố định cho một mẫu tin nhưng chưa bấm
                  Lưu. Rời khỏi đây (đóng dòng, đổi phạm vi hoặc đổi tab) sẽ bỏ các giá trị đó.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setConfirmLeave(null)}
                >
                  Ở lại
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => {
                    const run = confirmLeave?.run;
                    setConfirmLeave(null);
                    run?.();
                  }}
                >
                  Bỏ thay đổi
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog
            open={!!confirmOtpOff}
            onOpenChange={(o) => !o && setConfirmOtpOff(null)}
          >
            <DialogContent data-testid="confirm-otp-off">
              <DialogHeader>
                <DialogTitle>Tắt “Mã xác thực”?</DialogTitle>
                <DialogDescription>
                  Website đang cho bệnh nhân tra cứu kết quả khám bằng mã xác thực. Tắt hành
                  động này thì website ẩn mục tra cứu ngay — bệnh nhân không nhận được mã nữa. Ô
                  “Cho phép tra cứu” ở trang Website vẫn giữ nguyên, và mục tra cứu tự hiện lại
                  khi bật lại hành động này.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setConfirmOtpOff(null)}
                >
                  Giữ bật
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => {
                    const row = confirmOtpOff;
                    setConfirmOtpOff(null);
                    if (row) {
                      saveEvent.mutate({
                        row,
                        templateCode: row.templateCode,
                        enabled: false,
                      });
                    }
                  }}
                >
                  Tắt mã xác thực
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}
    </div>
  );
}

/**
 * "7 ngày qua: 52 đã gửi · 3 bị từ chối" (#19566) — the outcome under the prediction. Replaces
 * "Kiểm tra lần cuối", which was the time of the last save and read as a check nobody ran.
 */
const CHANNEL_SHORT: Record<string, string> = { ZBS: "Zalo ZBS", SMS: "SMS", EMAIL: "Email" };

function DeliveryLine({
  totals,
  days,
}: {
  totals: ReturnType<typeof totalsFor>;
  days: number;
}) {
  // "đã gửi" = the provider took it. Accepted-but-unanswered is a wait, not a success (#19642).
  const parts: string[] = [`${totals.ok} đã gửi`];
  if (totals.queued) parts.push(`${totals.queued} chờ phản hồi`);
  if (totals.failed) parts.push(`${totals.failed} bị từ chối/lỗi`);
  if (totals.notSent) parts.push(`${totals.notSent} không gửi được`);
  if (totals.expired) parts.push(`${totals.expired} quá hạn`);
  const bad = totals.failed > 0 || totals.notSent > 0;
  const title = totals.failures
    .map((f) => `${failureLabel(f.code, f.channel)}: ${f.count}`)
    .join("\n");
  return (
    <small
      className={
        "text-xs " + (bad ? "text-amber-800" : "text-muted-foreground")
      }
      title={title || undefined}
      data-testid="delivery-line"
    >
      {days} ngày qua: {parts.join(" · ")}
      {totals.byChannel.length > 1 && (
        <span className="block text-xs" data-testid="delivery-by-channel">
          {totals.byChannel
            .map(
              (c) =>
                `${CHANNEL_SHORT[c.channel] ?? c.channel}: ${c.ok} đã gửi` +
                (c.queued ? ` · ${c.queued} chờ` : "") +
                (c.failed ? ` · ${c.failed} lỗi` : ""),
            )
            .join("  |  ")}
        </span>
      )}
      {totals.failures.length > 0 && (
        <span className="block text-xs">
          Lý do:{" "}
          {totals.failures
            .slice(0, 2)
            .map((f) => `${failureLabel(f.code, f.channel)} (${f.count})`)
            .join(", ")}
          {totals.failures.length > 2 ? "…" : ""}
        </span>
      )}
    </small>
  );
}

/**
 * Staff notifications (#19578): no template, no channel choice — who receives it and how, with
 * the same on/off switch as the patient table.
 */
function StaffEvents({
  events,
  saving,
  onToggle,
}: {
  events: NotificationEventRow[];
  saving: boolean;
  onToggle: (ev: NotificationEventRow, enabled: boolean) => void;
}) {
  return (
    <div className="grid" data-testid="staff-events">
      <div className="hidden grid-cols-[minmax(220px,1.3fr)_1fr_1fr] gap-x-4 border-b pb-2 text-xs font-medium text-muted-foreground lg:grid">
        <span>Sự kiện</span>
        <span>Người nhận</span>
        <span>Kênh</span>
      </div>
      {events.map((ev) => (
        <div
          key={ev.eventKey}
          id={`event-${ev.eventKey}`}
          className="grid gap-x-4 gap-y-1 border-b px-2 py-2.5 last:border-b-0 lg:grid-cols-[minmax(220px,1.3fr)_1fr_1fr] lg:items-center"
        >
          <span className="flex items-center gap-2.5">
            <Switch
              checked={ev.enabled}
              disabled={saving}
              aria-label={`Bật ${ev.label}`}
              onCheckedChange={(v) => onToggle(ev, v)}
            />
            <span className="text-sm font-medium">{ev.label}</span>
          </span>
          <span className="text-sm text-muted-foreground">
            Người phụ trách; nếu không có thì nhân viên có quyền{" "}
            <code className="rounded bg-muted px-1 text-xs">
              {ev.staffSelector ?? "(chưa cấu hình)"}
            </code>
          </span>
          <span className="text-sm text-muted-foreground">
            Trong ứng dụng + email nội bộ
          </span>
        </div>
      ))}
    </div>
  );
}
